import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { resetTaskTeam } from '../hooks/useTaskTeam';

interface User {
  id: string;
  name: string;
  email: string;
  email_verified?: boolean;
  role?: string;
  company_id?: string;
  business_name?: string;
  business_type?: string;
  isSuperAdmin?: boolean;
  profile_image?: string;
  onboarding_status?: 'pending' | 'in_progress' | 'completed' | 'suspended';
  onboarding_deadline?: string;
  // Company context (from /api/auth/me). Used to gate the reseller dashboard
  // (company.company_type === 'reseller' && company.reseller_status === 'approved')
  // and to read white-label branding.
  company?: {
    id?: string;
    name?: string;
    company_type?: 'standard' | 'parent' | 'subsidiary' | 'reseller';
    reseller_status?: 'pending' | 'approved' | 'rejected' | 'suspended' | null;
    branding_mode?: 'white_label' | 'powered_by' | 'probizpay';
    logo_url?: string;
  } | null;
  // Effective white-label branding for the app chrome (from /me). For a reseller's
  // customer this is the RESELLER's brand; for a direct customer logoUrl is null
  // (frontend falls back to ProBizPay). poweredBy => show "Powered by ProBizPay".
  platformBranding?: {
    logoUrl: string | null;
    name: string;
    brandingMode: 'white_label' | 'powered_by' | 'probizpay';
    poweredBy: boolean;
  };
  // Reseller staff only (from /me): admin = owner or Admin-access seller.
  resellerAccess?: {
    isAdmin: boolean;
    repAccessMode: 'commission_only' | 'account_manager' | null;
  };
}

interface LoginResult {
  success: boolean;
  needsVerification?: boolean;
  email?: string;
  requiresTwoFactor?: boolean;
  twoFactorMethod?: 'totp' | 'sms';
  twoFactorToken?: string;
  maskedPhone?: string;
}

interface TwoFactorResult {
  success: boolean;
  error?: string;
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<LoginResult>;
  verifyTwoFactor: (twoFactorToken: string, code: string, isBackupCode?: boolean) => Promise<TwoFactorResult>;
  logout: () => void;
  isLoading: boolean;
  /**
   * Re-read the signed-in user from the server.
   *
   * The user object is loaded ONCE at page load and never updated, so anything
   * the backend changes about the user mid-session is invisible to the app
   * until a full reload. That is what made onboarding loop: /onboarding/complete
   * set onboarding_status to 'completed' server-side, the page navigated to
   * /dashboard, and PrivateRoute then read the STALE 'pending' still in context
   * and bounced straight back to /onboarding.
   */
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Fetch the full user profile (/me) to enrich the flat login response with the
// company object (company_type / reseller_status / branding_mode) the reseller
// dashboard needs. Falls back to the partial login user if /me fails.
async function hydrateUser(token: string, fallback: User): Promise<User> {
  try {
    const res = await fetch('/api/auth/me', {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (res.ok) {
      const data = await res.json();
      if (data.user) return data.user as User;
    }
  } catch (_) {
    /* ignore — fall back to the partial user object */
  }
  return fallback;
}

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in on app start
    const token = localStorage.getItem('token');
    if (token) {
      // Verify token with backend
      fetch('/api/auth/me', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      .then(response => {
        if (!response.ok) throw new Error('Token invalid');
        return response.json();
      })
      .then(data => {
        if (data.user) {
          setUser(data.user);
        } else {
          localStorage.removeItem('token');
        }
      })
      .catch(() => {
        localStorage.removeItem('token');
      })
      .finally(() => {
        setIsLoading(false);
      });
    } else {
      setIsLoading(false);
    }
  }, []);

  const login = async (email: string, password: string): Promise<LoginResult> => {
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      if (response.ok) {
        const data = await response.json();

        // Check if 2FA is required
        if (data.requiresTwoFactor) {
          return {
            success: false,
            requiresTwoFactor: true,
            twoFactorMethod: data.twoFactorMethod,
            twoFactorToken: data.twoFactorToken,
            maskedPhone: data.maskedPhone
          };
        }

        // Check if email is verified
        if (data.user && !data.user.email_verified) {
          return { success: false, needsVerification: true, email: data.user.email };
        }

        localStorage.setItem('token', data.token);
        // Hydrate the full profile (the login response is a flat subset and omits
        // the company object). /me returns company + company_type/reseller_status,
        // which the reseller dashboard gate needs immediately post-login.
        setUser(await hydrateUser(data.token, data.user));
        return { success: true };
      }
      return { success: false };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false };
    }
  };

  const verifyTwoFactor = async (twoFactorToken: string, code: string, isBackupCode = false): Promise<TwoFactorResult> => {
    try {
      const response = await fetch('/api/auth/verify-2fa', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ twoFactorToken, code, isBackupCode })
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('token', data.token);
        setUser(await hydrateUser(data.token, data.user));
        return { success: true };
      }

      return { success: false, error: data.error || 'Verification failed' };
    } catch (error) {
      console.error('2FA verification error:', error);
      return { success: false, error: 'Verification failed' };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    // Logout does not reload the page, so anything cached at module scope
    // outlives the session. The task team/permission cache must go, or the next
    // person to log in on this tab inherits the previous user's `me` id and
    // manager flag (the server still enforces, but the UI would lie).
    resetTaskTeam();
    setUser(null);
  };

  // Global fetch interceptor: auto-logout on account suspension/deletion/restriction
  useEffect(() => {
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const response = await originalFetch(...args);
      // Skip interceptor for customer portal routes
      if (response.status === 403 && !window.location.pathname.startsWith('/customer-portal')) {
        const cloned = response.clone();
        try {
          const data = await cloned.json();
          const kickCodes = ['ACCOUNT_SUSPENDED', 'ACCOUNT_DELETED', 'CUSTOMER_ROLE_RESTRICTED', 'ONBOARDING_SUSPENDED'];
          if (data.code && kickCodes.includes(data.code)) {
            localStorage.removeItem('token');
            setUser(null);
            const message = encodeURIComponent(data.error || 'Your account access has been revoked. Please contact ProBizPay at support@probizpay.com for assistance.');
            window.location.href = `/login?kicked=true&message=${message}`;
          }
          // Redirect to onboarding if required
          if (data.code === 'ONBOARDING_REQUIRED' && !window.location.pathname.startsWith('/onboarding')) {
            window.location.href = '/onboarding';
          }
          // Redirect to email verification if needed
          if (data.code === 'EMAIL_NOT_VERIFIED' && !window.location.pathname.startsWith('/verify')) {
            window.location.href = '/verify-email-required';
          }
          // Redirect to subscription page if trial expired or subscription canceled
          if ((data.code === 'TRIAL_EXPIRED' || data.code === 'SUBSCRIPTION_CANCELED') && !window.location.pathname.startsWith('/subscription')) {
            window.location.href = '/subscription?expired=true';
          }
        } catch (e) {
          // Not JSON, ignore
        }
      }
      return response;
    };
    return () => {
      window.fetch = originalFetch;
    };
  }, []);

  // Re-fetch the current user. Same call the app makes at boot, so whatever
  // /api/auth/me returns is what the guards will see.
  const refreshUser = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const response = await fetch('/api/auth/me', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) return;
      const data = await response.json();
      if (data.user) setUser(data.user);
    } catch (err) {
      // A failed refresh must not sign anyone out — the existing session is
      // still valid, it is only this snapshot that could not be updated.
      console.error('Could not refresh the signed-in user:', err);
    }
  };

  const value = {
    user,
    login,
    verifyTwoFactor,
    logout,
    isLoading,
    refreshUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
